import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Skeleton, Spinner } from "../components/Loaders";
import { EmptyState, PageIntro } from "../components/Visual";
import { Icon } from "../components/Icons";
import type { SavedDeck, SavedDeckSummary } from "../types";
import { DeckCardArt, DeckSectionsReadonly, firstDeckCardArt } from "./SavedDecks";

export function PublicDecks() {
  const [decks, setDecks] = useState<SavedDeckSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<SavedDeckSummary[]>("/api/public/decks?limit=100");
      setDecks(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <main className="page community-page">
      <PageIntro
        eyebrow="Community decks"
        icon="grid"
        title={<>Browse the <span className="grad">public metavault.</span></>}
        body="Open a published list to copy Arena text, save your own copy, run an analysis, or use it as the seed for a new build."
        actions={<Link to="/build" className="topbar-btn primary"><Icon name="wand" size={16} />Build a new deck</Link>}
      />

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="deck-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="deck-card" key={i}>
              <Skeleton variant="title" width="70%" style={{ marginBottom: 10 }} />
              <Skeleton variant="text" width="50%" style={{ marginBottom: 6 }} />
              <Skeleton variant="text" width="40%" />
            </div>
          ))}
        </div>
      ) : decks && decks.length === 0 ? (
        <EmptyState
          icon="cards"
          title="No public decks yet"
          body="Publish a saved deck and it will show up here for everyone."
          action={<Link to="/decks" className="topbar-btn">Open saved decks</Link>}
        />
      ) : (
        <div className="deck-grid">
          {(decks || []).map((d) => (
            <PublicDeckCard
              key={d.id}
              deck={d}
            />
          ))}
        </div>
      )}
    </main>
  );
}

function PublicDeckCard({
  deck,
}: {
  deck: SavedDeckSummary;
}) {
  const navigate = useNavigate();
  const colors = (deck.theme?.colors || []).join("") || "-";
  const main = deck.totals?.mainboard ?? 0;
  const side = deck.totals?.sideboard ?? 0;
  const published = deck.publishedAt
    ? new Date(deck.publishedAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

  return (
    <article
      className="deck-card deck-card--clickable deck-card--community"
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/community/${encodeURIComponent(deck.id)}`)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          navigate(`/community/${encodeURIComponent(deck.id)}`);
        }
      }}
    >
      <div className="community-card-media">
        <DeckCardArt deck={deck} />
        <div className="community-card-badges">
          <span className="deck-meta-pill public">Public</span>
          <span className="deck-meta-pill">{deck.format || "-"}</span>
        </div>
      </div>

      <div className="community-card-body">
        <header className="deck-card-head">
          <div>
            <p className="community-card-kicker">{deck.theme?.archetype || "Community brew"}</p>
            <h3>{deck.name}</h3>
          </div>
          <span className="colors" aria-label={`Colors ${colors}`}>{colors}</span>
        </header>

        <div className="community-card-stats">
          <span className="pill">Main {main}</span>
          {side > 0 && <span className="pill">Side {side}</span>}
          <span className="community-card-author">{deck.authorName || "Anonymous brewer"}</span>
        </div>

        {deck.query && <p className="deck-card-query muted">{deck.query}</p>}

        <div className="community-card-footer">
          <div className="deck-public-meta">
            {published && <span>Published {published}</span>}
          </div>
          <div className="deck-card-actions">
            <Link
              to={`/analyze?publicDeck=${encodeURIComponent(deck.id)}`}
              className="topbar-btn small-link"
              onClick={(event) => event.stopPropagation()}
            >
              <Icon name="chart" size={14} />
              Analyze
            </Link>
            <Link
              to={`/build?publicDeck=${encodeURIComponent(deck.id)}`}
              className="topbar-btn small-link"
              onClick={(event) => event.stopPropagation()}
            >
              <Icon name="wand" size={14} />
              Build off
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function PublicDeckView({
  id,
}: {
  id: string;
}) {
  const { firebaseUser } = useAuth();
  const navigate = useNavigate();
  const [deck, setDeck] = useState<SavedDeck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDeck(null);
    setError(null);
    setCopyState("idle");
    setSaveState("idle");
    setSaveError(null);
    apiGet<SavedDeck>(`/api/public/decks/${encodeURIComponent(id)}`)
      .then((d) => {
        if (!cancelled) setDeck(d);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const copyArena = async () => {
    if (!deck?.arena_text) return;
    try {
      await navigator.clipboard.writeText(deck.arena_text);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1600);
    } catch (e) {
      alert("Copy failed: " + (e as Error).message);
    }
  };

  const saveCopy = async () => {
    if (!deck) return;
    if (!firebaseUser) {
      navigate("/login", { state: { from: "/community" } });
      return;
    }
    setSaveState("saving");
    setSaveError(null);
    try {
      await apiPost<SavedDeckSummary>("/api/decks", {
        name: `Copy of ${deck.name}`,
        format: deck.format || "standard",
        query: deck.query || "",
        published: false,
        deck: deck.deck || {},
        arena_text: deck.arena_text || "",
        rationale: deck.rationale || "",
        theme: deck.theme || {},
        scenarios: deck.scenarios || {},
        totals: deck.totals || {},
        rarity_counts: deck.rarity_counts || {},
        caps: deck.caps || {},
        sets: deck.sets || [],
        set_conformance: deck.set_conformance || {},
        notes: deck.notes || [],
      });
      setSaveState("saved");
    } catch (e) {
      setSaveError((e as Error).message);
      setSaveState("error");
    }
  };

  return (
    <section className="saved-deck-view rationale-block deck-detail-shell">
      <div className="deck-detail-hero">
        <DeckCardArt deck={deck} card={firstDeckCardArt(deck?.deck)} />
        <div>
          <div className="deck-detail-kicker">Community deck</div>
          <h3>
            {deck?.name ? deck.name : <Skeleton variant="title" width={180} />}
          </h3>
          {deck && (
            <div className="deck-public-meta" style={{ marginTop: 4 }}>
              <span>{deck.authorName || "Anonymous brewer"}</span>
              <span>{deck.format || "standard"}</span>
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {saveError && <div className="error-banner">{saveError}</div>}

      {!deck && !error && (
        <>
          <Skeleton variant="text" width="80%" style={{ marginBottom: 8 }} />
          <Skeleton variant="block" height={120} />
        </>
      )}

      {deck && (
        <>
          <div className="public-deck-view-actions">
            <button className="primary" onClick={copyArena} disabled={!deck.arena_text}>
              <Icon name="clipboard" size={16} />
              {copyState === "copied" ? "Copied" : "Copy MTGA"}
            </button>
            <button
              className="secondary"
              onClick={saveCopy}
              disabled={saveState === "saving" || saveState === "saved"}
            >
              {saveState === "saving" ? (
                <>
                  <Spinner className="button-spinner" />
                  Saving...
                </>
              ) : saveState === "saved" ? (
                "Saved copy"
              ) : (
                <><Icon name="archive" size={16} />Save copy</>
              )}
            </button>
            <Link
              to={`/analyze?publicDeck=${encodeURIComponent(deck.id)}`}
              className="topbar-btn"
            >
              <Icon name="chart" size={16} />
              Analyze
            </Link>
            <Link
              to={`/build?publicDeck=${encodeURIComponent(deck.id)}`}
              className="topbar-btn"
            >
              <Icon name="wand" size={16} />
              Build off
            </Link>
            {saveState === "saved" && (
              <Link to="/decks" className="topbar-btn">View saved</Link>
            )}
          </div>

          {deck.rationale && (
            <div className="rationale-block" style={{ marginTop: 0 }}>
              <h4>Rationale</h4>
              <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, margin: 0 }}>
                {deck.rationale}
              </p>
            </div>
          )}

          <div className="deck-view">
            <h3>Decklist</h3>
            <DeckSectionsReadonly deck={deck.deck} />
          </div>

          {deck.arena_text && (
            <div className="arena-block">
              <div className="arena-header">
                <h3>MTG Arena import text</h3>
                <button className="secondary" onClick={copyArena}>
                  {copyState === "copied" ? "Copied" : "Copy to clipboard"}
                </button>
              </div>
              <pre className="arena-text" spellCheck={false}>
                {deck.arena_text}
              </pre>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function PublicDeckDetailsPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return (
      <main className="page community-page">
        <div className="error-banner">Deck not found.</div>
      </main>
    );
  }
  return (
    <main className="page community-page">
      <PageIntro
        eyebrow="Community decks"
        icon="cards"
        title={<>Deck <span className="grad">details</span></>}
        body="Inspect the full list details and actions for this public deck."
        actions={<Link to="/community" className="topbar-btn">Back to community decks</Link>}
      />
      <PublicDeckView id={id} />
    </main>
  );
}
